import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import PageFit from "@/components/kit/PageFit";
import RoleBullets from "@/components/kit/RoleBullets";
import { CopyButton, Field, Section } from "@/components/kit/KitFields";
import FileCheck from "@/components/ats/FileCheck";
import { api, errMsg } from "@/lib/api";

function useCopied(jobId) {
  const key = `kit-copied-${jobId}`;
  const [copied, setCopied] = useState(() => new Set(JSON.parse(localStorage.getItem(key) || "[]")));
  const save = (s) => { localStorage.setItem(key, JSON.stringify([...s])); return s; };
  const mark = (k) => setCopied((p) => save(new Set(p).add(k)));
  const clear = () => setCopied(save(new Set()));
  return { copied, mark, clear };
}

export default function CopyKit({ job }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const kit = useCopied(job.id);
  const [side, setSide] = useState(() => localStorage.getItem("kit-side") !== "0");
  const toggleSide = () => setSide((s) => { localStorage.setItem("kit-side", s ? "0" : "1"); return !s; });
  const load = useCallback(() => api.get(`/jobs/${job.id}/kit`).then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e))), [job.id]);
  useEffect(() => { load(); }, [load]);
  if (err && !d) return <div className="card-surface text-sm text-rose-300" data-testid="kit-error">{err}</div>;
  if (!d) return <div className="card-surface text-sm text-slate-400" data-testid="kit-loading">Ranking bullets…</div>;
  const c = d.content, h = c.header;
  const [first, ...rest] = (h.name || "").split(" ");
  const reset = async () => {
    try { const { data } = await api.post(`/jobs/${job.id}/kit/rebuild`); setD(data); toast.success("Picks reset to the ranked 5"); } catch (e) { toast.error(errMsg(e)); }
  };
  const F = (label, value, k, testid) => <Field label={label} value={value} k={k} kit={kit} testid={testid} />;
  const skillsAll = c.skills_groups.flatMap((g) => g.items).join(", ");
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start" data-testid="copy-kit">
    <div className="space-y-4 min-w-0">
      <div className="flex items-center gap-3">
        <p className="text-sm text-slate-400 flex-1">Copy each piece into the matching Jobright field. Items you've copied turn green.</p>
        <button data-testid="kit-side-toggle" onClick={toggleSide}
          className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${side ? "bg-blue-600 border-blue-500 text-white" : "border-border text-slate-300 hover:border-blue-500/60"}`}>
          {side ? "Hide original resume" : "Show original resume side by side"}</button>
        <button data-testid="kit-clear-ticks" onClick={kit.clear} className="text-xs text-slate-400 hover:text-white">Clear ticks</button>
        <button data-testid="kit-reset-picks" onClick={reset} className="text-xs inline-flex items-center gap-1 text-slate-400 hover:text-white"><RotateCcw size={12} />Reset picks</button>
      </div>
      <Section testid="kit-contact" title="Contact">
        {F("First name", first, "c:first", "kit-first-name")}{F("Last name", rest.join(" "), "c:last", "kit-last-name")}
        {F("Email", h.email, "c:email", "kit-email")}{F("Phone", h.phone, "c:phone", "kit-phone")}
        {F("Location", h.location_line, "c:loc", "kit-location")}{F("LinkedIn", h.linkedin, "c:li", "kit-linkedin")}
        {F("Portfolio", h.portfolio, "c:pf", "kit-portfolio")}{F("Work authorization", c.work_authorization, "c:wa", "kit-work-auth")}
      </Section>
      <Section testid="kit-summary" title="Headline & summary">
        {F("Headline", c.headline, "s:head", "kit-headline")}{F("Summary", c.summary, `s:sum:${c.summary.length}`, "kit-summary-text")}
        {c.years_statements?.length > 0 && F("Years of experience", c.years_statements.join("; "), "s:yrs", "kit-years")}
      </Section>
      <div className="label-xs pt-2">Experience</div>
      {c.experience.map((r) => <RoleBullets key={r.role_id} jobId={job.id} role={r} items={d.roles[r.role_id] || []} show={d.show[r.role_id] || 0} kit={kit} onData={setD} orig={d.originals?.[r.role_id]} side={side} />)}
      <Section testid="kit-education" title="Education">
        {c.education.map((e, i) => (
          <div key={i} className="mb-3 last:mb-0">
            {F("Degree", e.degree, `e:${i}:deg`, `kit-edu-${i}-degree`)}{F("School", e.school, `e:${i}:sch`, `kit-edu-${i}-school`)}
            {F("Location", e.location, `e:${i}:loc`, `kit-edu-${i}-location`)}{F("End date", e.end, `e:${i}:end`, `kit-edu-${i}-end`)}
            {e.detail && F("Details", e.detail, `e:${i}:det`, `kit-edu-${i}-detail`)}
          </div>
        ))}
      </Section>
      <Section testid="kit-skills" title="Skills" action={<CopyButton text={skillsAll} k="sk:all" kit={kit} testid="kit-skills-copy-all" label="Copy all skills" />}>
        {c.skills_groups.map((g, i) => F(g.category, g.items.join(", "), `sk:${i}`, `kit-skills-${i}`))}
      </Section>
      {c.certifications?.filter(Boolean).length > 0 && (
        <Section testid="kit-certs" title="Certifications">{c.certifications.filter(Boolean).map((x, i) => F(`Certification ${i + 1}`, x, `ct:${i}`, `kit-cert-${i}`))}</Section>
      )}
    </div>
    <div className="lg:sticky lg:top-[130px] space-y-3 order-first lg:order-none">
      <PageFit fit={d.fit} />
      <div className="card-surface text-xs text-slate-400 space-y-1.5" data-testid="kit-picks-summary">
        <div className="label-xs mb-1">Bullets picked</div>
        {c.experience.map((r) => <div key={r.role_id} className="flex gap-2"><span className="truncate flex-1">{r.title} · {r.company}</span><span className="text-slate-200 font-mono">{Math.min(d.show[r.role_id] || 0, (d.roles[r.role_id] || []).length)}/{(d.roles[r.role_id] || []).length}</span></div>)}
        <div className="pt-1 text-slate-500">Word and PDF downloads use these picks.</div>
      </div>
      <FileCheck jobId={job.id} />
    </div>
    </div>
  );
}
